import { useMemo, useState } from "react";
import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import {
  Badge,
  Card,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Skeleton,
  Slider,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { tripsApi, type TripTrailPoint } from "../../api/trips";
import { GOOGLE_MAPS_API_KEY } from "../../config/env";
import { tokensFor } from "../../theme/tokens";
import { QueryError } from "../common/QueryError";
import { formatDateTime } from "../../lib/format";
import { MAP_TOKENS } from "./LiveMap";

const TO_PICKUP = new Set(["accepted", "driverArrived"]);

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {value}
      </Text>
    </Stack>
  );
}

function minutesBetween(a: string, b: string): string {
  const minutes = Math.round((Date.parse(b) - Date.parse(a)) / 60000);
  return `${minutes} min`;
}

/**
 * Replays the driver's recorded GPS trail for a trip: the leg to pickup and
 * the trip itself, with a scrubber to see where the car was at any moment.
 * Trips from before trail recording have no points.
 */
export function TripRouteReplayCard({ tripId }: { tripId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trip-trail", tripId],
    queryFn: () => tripsApi.trail(tripId),
    enabled: !!tripId,
  });
  const points = data ?? [];
  const [index, setIndex] = useState<number | null>(null);

  return (
    <Card withBorder radius="md" padding="lg" mb="md">
      <Group justify="space-between" mb="md">
        <Title order={5}>Route replay</Title>
        {points.some((p) => p.source === "simulation") && (
          <Badge color="gray" variant="light">
            Simulated
          </Badge>
        )}
      </Group>
      {isLoading ? (
        <Skeleton h={320} />
      ) : error ? (
        <QueryError error={error} onRetry={refetch} />
      ) : points.length < 2 ? (
        <Text size="sm" c="dimmed">
          No GPS trail recorded for this trip.
        </Text>
      ) : (
        <ReplayBody
          points={points}
          index={index ?? points.length - 1}
          onIndexChange={setIndex}
        />
      )}
    </Card>
  );
}

function ReplayBody({
  points,
  index,
  onIndexChange,
}: {
  points: TripTrailPoint[];
  index: number;
  onIndexChange: (index: number) => void;
}) {
  const scheme = useComputedColorScheme("light");
  const tk = useMemo(() => tokensFor(scheme), [scheme]);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "safiri-admin-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const path = (list: TripTrailPoint[]) =>
    list.map((p) => ({ lat: p.latitude, lng: p.longitude }));
  const toPickup = points.filter((p) => TO_PICKUP.has(p.tripStatus ?? ""));
  const onTrip = points.filter((p) => !TO_PICKUP.has(p.tripStatus ?? ""));
  const current = points[index];
  const first = points[0];
  const last = points[points.length - 1];
  const center = { lat: current.latitude, lng: current.longitude };
  const bounds = useMemo(() => {
    const lats = points.map((p) => p.latitude);
    const lngs = points.map((p) => p.longitude);
    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };
  }, [points]);

  return (
    <Stack>
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Stat label="Points" value={points.length} />
        <Stat label="Recorded" value={minutesBetween(first.recordedAt, last.recordedAt)} />
        <Stat label="Started" value={formatDateTime(first.recordedAt)} />
        <Stat label="Ended" value={formatDateTime(last.recordedAt)} />
      </SimpleGrid>

      {!GOOGLE_MAPS_API_KEY || loadError ? (
        <Text size="sm" c="dimmed">
          Map unavailable.
        </Text>
      ) : !isLoaded ? (
        <Center h={320}>
          <Loader />
        </Center>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: 360, borderRadius: 8 }}
          center={center}
          zoom={14}
          onLoad={(map) => map.fitBounds(bounds, 40)}
          options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy" }}
        >
          {toPickup.length > 1 && (
            <PolylineF
              path={path(toPickup)}
              options={{ strokeColor: tk[MAP_TOKENS.assigned], strokeWeight: 4, strokeOpacity: 0.8 }}
            />
          )}
          {onTrip.length > 1 && (
            <PolylineF
              path={path(onTrip)}
              options={{ strokeColor: tk[MAP_TOKENS.onTrip], strokeWeight: 5 }}
            />
          )}
          <MarkerF position={{ lat: first.latitude, lng: first.longitude }} label="A" />
          <MarkerF position={{ lat: last.latitude, lng: last.longitude }} label="B" />
          <MarkerF position={center} title="Driver" zIndex={10} />
        </GoogleMap>
      )}

      <Stack gap={4}>
        <Slider
          min={0}
          max={points.length - 1}
          value={index}
          onChange={onIndexChange}
          label={null}
          aria-label="Replay position"
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {formatDateTime(current.recordedAt)}
          </Text>
          <Text size="xs" c="dimmed">
            {current.tripStatus ?? "—"}
            {current.speed != null ? ` · ${Math.round(current.speed * 3.6)} km/h` : ""}
          </Text>
        </Group>
      </Stack>
    </Stack>
  );
}
